import { datadogRum } from '@datadog/browser-rum'
import { PaymentServiceType } from '@dni-types/data-models/ecommerce-payment'
import type * as TapPay from '@dni-types/payment/tappay'
import { cardFields, cardholderFields } from '../constants/tappay'
import { PAYMENT_ERROR_CODE, PaymentError } from './PaymentError'
import { type PaymentModule, type PaymentSetting } from './types'
import { isTappaySdkLoaded, loadTappayScript, tappayRemoveExistingIframes } from './utilities'

const styles = {
  'input': {
    'color': '#9E9E9E', // grey500
    'font-size': '14px',
  },
  'input.card-number.invalid': {
    color: '#FF5353', // red600
  },
  ':focus': {
    color: '#424242', // grey800
  },
  '::placeholder': {
    color: '#9E9E9E', // grey500
  },
  '.invalid': {
    color: 'red',
  },
}

type EventCallbackType = 'cardUpdate'
type EventCallback<EventCallbackType> = EventCallbackType extends 'cardUpdate' ? (data: TapPay.CardUpdate) => void : never

export class TappayCreditCardModule implements PaymentModule<PaymentSetting> {
  isLoaded = false
  appEnv: PaymentSetting['appEnv'] = 'sandbox'
  appId: Required<PaymentSetting>['appId'] = ''
  appKey: Required<PaymentSetting>['appKey'] = ''
  card: TapPay.ValidationResult['card'] = undefined
  merchantReferenceInfo: TapPay.ValidationResult['merchant_reference_info'] = undefined
  cardIdentifier: TapPay.ValidationResult['card_identifier'] | undefined = undefined
  eventListeners: Record<EventCallbackType, Set<EventCallback<EventCallbackType>>> = {
    cardUpdate: new Set(),
  }

  private static instance: TappayCreditCardModule | null = null

  // Static getInstance method for singleton pattern
  public static getInstance(): TappayCreditCardModule {
    if (!TappayCreditCardModule.instance) {
      TappayCreditCardModule.instance = new TappayCreditCardModule()
    }
    return TappayCreditCardModule.instance
  }

  public loadScript = () => loadTappayScript().then(() => {
    this.isLoaded = true
  })

  private pendingConfigureDetails: PaymentSetting | null = null

  /**
   * SDK 尚未就緒時暫存最新一次的 configure 參數，待載入完成後補跑
   * setupSDK + setupPaymentUi（掛卡號 iframe），否則欄位永遠不會出現
   */
  private deferConfigurePayment(details: PaymentSetting) {
    const isFirstDefer = this.pendingConfigureDetails === null
    this.pendingConfigureDetails = details
    if (!isFirstDefer) return
    loadTappayScript()
      .then(() => {
        const pendingDetails = this.pendingConfigureDetails
        this.pendingConfigureDetails = null
        if (pendingDetails && isTappaySdkLoaded(window.TPDirect)) {
          this.configurePayment(pendingDetails)
        }
      })
      .catch((error) => {
        // 清掉暫存讓之後的 configurePayment 能重新 defer；錯誤要留紀錄，否則又是無聲死亡
        this.pendingConfigureDetails = null
        console.error('[Tappay Credit Card] deferred configurePayment failed', error)
        datadogRum.addError(error, { function: 'TappayCreditCardModule.deferConfigurePayment' })
      })
  }

  public configurePayment(details: PaymentSetting) {
    console.debug('[Tappay Credit Card] configurePayment details:', details)
    if (!isTappaySdkLoaded(window.TPDirect)) {
      this.deferConfigurePayment(details)
      return
    }
    // 直接執行的 configure 永遠代表最新意圖，取消還在排隊的 deferred configure，避免舊參數晚到覆蓋
    this.pendingConfigureDetails = null
    if (!details.appId || !details.appKey) {
      throw new PaymentError('[Tappay Credit Card] appId or appKey is required', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_APP_ID_OR_APP_KEY)
    }

    try {
      // 如果 appId 或 appKey 不同，則重新配置
      if (this.appId !== details.appId || this.appKey !== details.appKey) {
        this.appEnv = details.appEnv
        this.appId = details.appId
        this.appKey = details.appKey
        window.TPDirect.setupSDK(this.appId, this.appKey, this.appEnv)
      }
      this.setupPaymentUi()
    }
    catch (error) {
      throw new PaymentError(`[Tappay Credit Card] Configuration failed: ${error}`, PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.CONFIGURATION_FAILED, error)
    }
  }

  public setupPaymentUi(options?: { includeCardholder?: boolean }) {
    if (!isTappaySdkLoaded(window.TPDirect)) return
    tappayRemoveExistingIframes()
    const includeCardholder = options?.includeCardholder ?? true
    const setupConfig = includeCardholder
      ? { fields: cardFields, cardholder: cardholderFields, styles }
      : { fields: cardFields, styles }
    window.TPDirect.card.setup(setupConfig)
  }

  public reset() {
    this.appEnv = 'sandbox'
    this.appId = ''
    this.appKey = ''
    this.card = undefined
    this.merchantReferenceInfo = undefined
    this.cardIdentifier = undefined
    // 使用者已離開信用卡流程，取消排隊中的 deferred configure，避免 SDK 晚到後對已 unmount 的容器補跑 setup
    this.pendingConfigureDetails = null
  }

  public getPrime(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!isTappaySdkLoaded(window.TPDirect)) return
      try {
        window.TPDirect.card.getPrime((result) => {
          console.debug('[Tappay Credit Card] getPrime result', result)
          if (result.status !== 0) {
            reject(new PaymentError('[Tappay Credit Card] status errors', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_PRIME, result))
            return
          }
          if (!result.card?.prime) {
            reject(new PaymentError('[Tappay Credit Card] invalid prime', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_PRIME, result))
            return
          }
          this.card = result.card
          this.merchantReferenceInfo = result.merchant_reference_info
          this.cardIdentifier = result.card_identifier
          resolve(result.card.prime)
        })
      }
      catch (error) {
        reject(new PaymentError(`[Tappay Credit Card] Failed to get prime: ${error}`, PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.GET_PRIME_ERROR, error))
      }
    })
  }

  public getCard(): TapPay.ValidationResult['card'] {
    return this.card
  }

  public getMerchantReferenceInfo(): TapPay.ValidationResult['merchant_reference_info'] {
    return this.merchantReferenceInfo
  }

  public getCardIdentifier(): TapPay.ValidationResult['card_identifier'] | undefined {
    return this.cardIdentifier
  }

  private hasDeferredRegistration = false

  private registerEventListeners() {
    console.debug('[Tappay Credit Card] registerEventListeners', window.TPDirect)
    if (!isTappaySdkLoaded(window.TPDirect)) {
      this.deferRegisterEventListeners()
      return
    }
    window.TPDirect.card.onUpdate((data) => {
      this.eventListeners.cardUpdate.forEach(cb => cb(data))
    })
  }

  /**
   * SDK 可能仍在載入中（慢速網路下 script 晚於 React effect 就緒）：
   * 等載入完成後補掛 onUpdate，否則卡片驗證永遠不會觸發、送出鈕永久 disabled
   */
  private deferRegisterEventListeners() {
    if (this.hasDeferredRegistration) return
    this.hasDeferredRegistration = true
    loadTappayScript()
      .then(() => {
        this.registerEventListeners()
      })
      .catch((error) => {
        // 重設 flag 讓之後的 add/removeEventListener 能重新 defer；錯誤要留紀錄
        this.hasDeferredRegistration = false
        console.error('[Tappay Credit Card] deferred registerEventListeners failed', error)
        datadogRum.addError(error, { function: 'TappayCreditCardModule.deferRegisterEventListeners' })
      })
  }

  public addEventListener <T extends EventCallbackType>(type: T, cb: EventCallback<T>) {
    console.debug('[Tappay Credit Card] addEventListener', type)
    this.eventListeners[type].add(cb)
    this.registerEventListeners()
  }

  public removeEventListener <T extends EventCallbackType>(type: T, cb: EventCallback<T>) {
    console.debug('[Tappay Credit Card] removeEventListener', type)
    this.eventListeners[type].delete(cb)
    this.registerEventListeners()
  }
}
